"use client";

import { signIn } from "next-auth/react";
import { Suspense, useEffect, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import { HouseBackdrop } from "@/components/HouseBackdrop";
import { BRAND } from "@/lib/brand";

/** Hilsen som følger tid på døgnet – huset bak endrer seg i takt. */
function greeting(hour: number): string {
  if (hour >= 5 && hour < 10) return "God morgen";
  if (hour >= 10 && hour < 17) return "Hei";
  if (hour >= 17 && hour < 22) return "God kveld";
  return "God natt";
}

/** Husker hvem som bruker denne telefonen, slik at Katarina slipper Eiriks e-post. */
const SISTE_EPOST = "ainstein:siste-epost";

function LoginForm() {
  /**
   * Starter TOM med vilje.
   *
   * Feltet var hardkodet til "eirik@localhost". Da React hydrerte, ble
   * inputen tilbakestilt til den verdien – så rakk du å taste e-posten din før
   * hydreringen var ferdig, ble den byttet ut. På telefon over Funnel, der
   * hydreringen tar tid, skjedde det lett.
   *
   * Siste brukte e-post fylles inn fra localStorage etterpå, men bare hvis
   * feltet fortsatt er urørt.
   */
  const [email, setEmail] = useState("");
  const [rørtEpost, setRørtEpost] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  /*
    Trinn to i innloggingen: koden som slipper en ny nettleser inn.

    Passordet blir stående i skjemaet mens koden tastes. Det er med vilje:
    alternativet var å be om det en gang til etter koden, og et passord som
    må skrives to ganger på en telefon blir et passord man forenkler.
  */
  const [kodeSteg, setKodeSteg] = useState(false);
  const [kode, setKode] = useState("");
  const [kodeHvor, setKodeHvor] = useState("");
  const [oauthLoading, setOauthLoading] = useState<string | null>(null);
  const [providers, setProviders] = useState<Record<string, { id: string; name: string }> | null>(null);
  const [hilsen, setHilsen] = useState("Velkommen hjem");
  const params = useSearchParams();
  const err = params.get("error");
  /*
    Tilbake dit man var – men bare på SAMME vert.

    Middleware sender «/lytt?stasjon» til «/login?callbackUrl=http://…/lytt?stasjon»,
    og før havnet alle på forsiden etterpå. For stue-stasjonen betyr det at
    Chrome-vinduet uten adresselinje står på «I dag» etter første innlogging,
    og ingen kommer seg til /lytt uten å lete. Bare stien og spørringen tas
    med, og bare når adressen peker på verten vi står på: en adresse fra
    tunnelen mens man er på localhost, eller motsatt, ville sendt økta dit
    den ikke finnes.
  */
  const tilbakeTil = params.get("callbackUrl") ?? "";
  function hjem(): string {
    if (tilbakeTil.startsWith("/") && !tilbakeTil.startsWith("//")) return tilbakeTil;
    try {
      const u = new URL(tilbakeTil, window.location.origin);
      if (u.origin === window.location.origin) return u.pathname + u.search;
    } catch {
      /* ikke en adresse */
    }
    return "/";
  }

  const epostRef = useRef<HTMLInputElement>(null);
  const passordRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setHilsen(greeting(new Date().getHours()));
  }, []);

  /*
    Ta vare på det som allerede STÅR i feltene når React tar over.

    Feltene er styrt av React (`value={email}`). Fram til siden er hydrert er
    de vanlige HTML-felter, og det man rekker å skrive – eller det
    passordbehandleren fyller inn – ligger bare i DOM-en. Første gang React
    tegner på nytt, settes verdien tilbake til tilstanden, som er tom, og
    teksten forsvinner uten et ord.

    Målt i testen: felt fylt rett etter at HTML-en var på plass sto tomme tre
    sekunder senere, og innloggingen feilet med «feil e-post eller passord»
    på et passord som var helt riktig. Over Funnel, på en telefon, er
    tidsvinduet ikke lite.

    Her leses feltene én gang og tilstanden settes til det som faktisk står
    der. `rørtEpost` settes med vilje, slik at den lagrede e-posten fra forrige
    gang ikke skriver over noe brukeren nettopp tastet.
  */
  useEffect(() => {
    const skrevetEpost = epostRef.current?.value;
    const skrevetPassord = passordRef.current?.value;
    if (skrevetEpost) {
      setRørtEpost(true);
      setEmail(skrevetEpost);
    }
    if (skrevetPassord) setPassword(skrevetPassord);
  }, []);

  // Fyll inn forrige e-post fra denne enheten – men aldri over noe brukeren
  // har begynt å skrive.
  useEffect(() => {
    if (rørtEpost) return;
    try {
      const siste = localStorage.getItem(SISTE_EPOST);
      if (siste) setEmail((nå) => (nå ? nå : siste));
    } catch {
      /* privat modus kan blokkere localStorage – da står feltet tomt */
    }
  }, [rørtEpost]);

  useEffect(() => {
    fetch("/api/auth/providers")
      .then((r) => r.json())
      .then((data) => setProviders(data || {}))
      .catch(() => setProviders({}));
  }, []);

  const hasGoogle = !!providers?.google;
  const hasMicrosoft = !!(providers?.["microsoft-entra-id"] || providers?.microsoft);
  const oauthReady = hasGoogle || hasMicrosoft;

  /** Selve innloggingen. Kalles når nettleseren er kjent, eller koden er godtatt. */
  async function fullførInnlogging() {
    const res = await signIn("credentials", {
      email: email.trim().toLowerCase(),
      password,
      redirect: false,
      // Always same-origin relative path (never absolute tunnel URL)
      callbackUrl: hjem(),
    });
    if (res?.error) {
      setError("Feil e-post eller passord.");
      setLoading(false);
      return;
    }
    // Never follow res.url if it points to another host (tunnel vs localhost)
    const target =
      res?.url && res.url.startsWith("/")
        ? res.url
        : res?.url &&
            typeof window !== "undefined" &&
            res.url.startsWith(window.location.origin)
          ? res.url
          : hjem();
    // Husk hvem som bruker denne telefonen til neste gang
    try {
      localStorage.setItem(SISTE_EPOST, email.trim().toLowerCase());
    } catch {
      /* ikke kritisk */
    }
    // Standardpassordet tvinger /bytt-passord. Gå dit direkte, så vi ikke
    // hopper via «I dag» som bare sender oss videre.
    const next = password === "familie-change-me" ? "/bytt-passord" : target;
    window.location.assign(next);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      /*
        Først: er dette en nettleser vi kjenner?

        Er den ny, sender serveren en kode til en enhet du allerede har, eller
        til e-posten din – og innloggingen stopper her til den er tastet. Se
        lib/ny-enhet.ts for hvorfor: passordet alene er ikke nok når appen står
        åpen mot internett.
      */
      const sjekk = await fetch("/api/login/sjekk", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ epost: email.trim().toLowerCase(), passord: password }),
      });
      const svar = (await sjekk.json().catch(() => ({}))) as {
        status?: string;
        push?: number;
        epost?: boolean;
      };

      if (svar.status === "kode") {
        /*
          Kom koden verken fram som varsel eller e-post, skal det staa noe man
          kan gjoere noe med. Den ligger i serverloggen paa familie-PC-en - det
          er noedloesningen, se lib/ny-enhet.ts - og da er svaret aa spoerre den
          som steller appen, ikke aa lure paa hvor det ble av den.
        */
        const veier: string[] = [];
        if (svar.push) veier.push("enhetene dine");
        if (svar.epost) veier.push("e-posten din");
        setKodeHvor(veier.join(" og "));
        setKodeSteg(true);
        setLoading(false);
        return;
      }

      if (svar.status !== "ok") {
        // Ikke røp gyldige innloggingsdetaljer – appen er tilgjengelig fra
        // internett, og «finnes ikke» og «feil passord» skal se likt ut.
        setError("Feil e-post eller passord.");
        setLoading(false);
        return;
      }

      await fullførInnlogging();
    } catch {
      setError("Noe gikk galt. Prøv igjen.");
      setLoading(false);
    }
  }

  async function sendKode(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/login/kode", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kode: kode.trim() }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        setError(data?.error || "Feil kode.");
        setLoading(false);
        return;
      }
      await fullførInnlogging();
    } catch {
      setError("Noe gikk galt. Prøv igjen.");
      setLoading(false);
    }
  }

  async function oauth(provider: string) {
    setOauthLoading(provider);
    setError("");
    try {
      await signIn(provider, { callbackUrl: "/" });
    } catch {
      setError("OAuth feilet. Bruk e-post/passord i stedet.");
      setOauthLoading(null);
    }
  }

  const inputClass =
    "mt-1.5 w-full rounded-2xl border border-white/25 bg-white/10 px-4 py-3.5 text-base text-white outline-none transition placeholder:text-white/40 focus:border-amber-300/60 focus:bg-white/15 focus:ring-2 focus:ring-amber-300/40";

  return (
    <main className="relative flex min-h-[100dvh] items-center justify-center overflow-hidden p-4 sm:p-6">
      <HouseBackdrop />

      <div className="relative z-10 w-full max-w-sm">
        {/* Huset er stedet – si det før innloggingsboksen */}
        <div className="mb-6 text-center [text-shadow:0_2px_12px_rgba(0,0,0,0.7)]">
          <p className="text-sm font-medium tracking-wide text-amber-200/90">{hilsen}</p>
          <h1 className="mt-1 text-4xl font-bold tracking-tight text-white">{BRAND.name}</h1>
          <p className="mt-2 text-sm text-white/80">{BRAND.tagline}</p>
        </div>

        <div className="glass space-y-5 rounded-3xl p-6 sm:p-7">
          {(error || err) && (
            <p
              role="alert"
              className="rounded-2xl border border-red-300/30 bg-red-500/20 px-3 py-2.5 text-center text-sm text-red-50"
            >
              {error ||
                (err === "Configuration"
                  ? "Google/Microsoft er ikke satt opp ennå. Bruk e-post under."
                  : "Innlogging feilet. Prøv e-post/passord.")}
            </p>
          )}

          {/* Trinn to: koden. Vises i stedet for skjemaet, ikke under det –
              to skjemaer på skjermen samtidig er to ting å velge mellom. */}
          {kodeSteg ? (
            <form onSubmit={sendKode} noValidate className="space-y-4">
              <p className="text-sm leading-snug text-white/80">
                {kodeHvor ? (
                  <>Dette er en nettleser vi ikke har sett før. Vi sendte en kode til {kodeHvor}.</>
                ) : (
                  <>
                    Dette er en nettleser vi ikke har sett før, og vi fikk ikke sendt koden noe
                    sted. Den står i loggen på familie-PC-en – spør den som steller appen.
                  </>
                )}
              </p>
              <label className="block text-xs font-semibold uppercase tracking-wide text-white/70">
                Kode
                <input
                  type="text"
                  required
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000000"
                  value={kode}
                  onChange={(e) => setKode(e.target.value)}
                  className={inputClass}
                  autoFocus
                />
              </label>
              <button
                type="submit"
                disabled={loading}
                className="w-full touch-manipulation rounded-2xl bg-amber-500 py-3.5 text-base font-semibold text-amber-950 shadow-lg shadow-amber-900/30 transition hover:bg-amber-400 active:bg-amber-600 disabled:opacity-50"
              >
                {loading ? "Sjekker…" : "Logg inn"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setKodeSteg(false);
                  setKode("");
                  setError("");
                }}
                className="w-full text-center text-xs text-white/60 underline-offset-2 hover:underline"
              >
                Tilbake
              </button>
            </form>
          ) : (
          /* Primary: credentials – always works */
          <form onSubmit={onSubmit} noValidate className="space-y-4">
            <label className="block text-xs font-semibold uppercase tracking-wide text-white/70">
              Navn eller e-post
              <input
                ref={epostRef}
                type="text"
                required
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                placeholder="eirik"
                value={email}
                onChange={(e) => {
                  setRørtEpost(true);
                  setEmail(e.target.value);
                }}
                className={inputClass}
                autoComplete="username"
              />
            </label>
            <label className="block text-xs font-semibold uppercase tracking-wide text-white/70">
              Passord
              <input
                ref={passordRef}
                type="password"
                required
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputClass}
                autoComplete="current-password"
              />
            </label>
            <button
              type="submit"
              disabled={loading}
              className="w-full touch-manipulation rounded-2xl bg-amber-500 py-3.5 text-base font-semibold text-amber-950 shadow-lg shadow-amber-900/30 transition hover:bg-amber-400 active:bg-amber-600 disabled:opacity-50"
            >
              {loading ? "Logger inn…" : "Logg inn"}
            </button>
          </form>
          )}

          <p className="text-center text-[11px] text-white/50">
            Du blir værende innlogget på denne enheten
          </p>

          {providers === null ? null : oauthReady ? (
            <>
              <div className="relative text-center text-[11px] uppercase tracking-wide text-white/40">
                <span className="glass relative z-10 rounded-full px-3 py-0.5">eller</span>
                <div className="absolute inset-x-0 top-1/2 border-t border-white/15" />
              </div>
              <div className="space-y-2">
                {hasGoogle && (
                  <button
                    type="button"
                    disabled={!!oauthLoading}
                    onClick={() => void oauth("google")}
                    className="flex w-full touch-manipulation items-center justify-center gap-2 rounded-2xl border border-white/25 bg-white/10 py-3.5 text-base font-medium text-white transition hover:bg-white/20 disabled:opacity-50"
                  >
                    {oauthLoading === "google" ? "Åpner Google…" : "Fortsett med Google"}
                  </button>
                )}
                {hasMicrosoft && (
                  <button
                    type="button"
                    disabled={!!oauthLoading}
                    onClick={() => void oauth("microsoft-entra-id")}
                    className="flex w-full touch-manipulation items-center justify-center gap-2 rounded-2xl border border-white/25 bg-white/10 py-3.5 text-base font-medium text-white transition hover:bg-white/20 disabled:opacity-50"
                  >
                    {oauthLoading === "microsoft-entra-id"
                      ? "Åpner Microsoft…"
                      : "Fortsett med Microsoft"}
                  </button>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </main>
  );
}

export default function LoginPage() {
  return (
    <Suspense
      fallback={
        <main className="flex min-h-[100dvh] items-center justify-center p-8 text-stone-500">
          Laster…
        </main>
      }
    >
      <LoginForm />
    </Suspense>
  );
}
